import {
  resolveAgentWorkbenchSidebarConfig,
  type AgentWorkbenchSessionItem,
  type AgentWorkbenchSidebarConfig,
} from "@inf-monkeys-tech/monkeys";
import {
  type ReactEventHandler,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";

import { cn } from "../layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../primitives";
import type { AgentWorkbenchVariant } from "./appearance";
import { AgentWorkbenchSidebarContainer } from "./AgentWorkbenchSidebarContainer";
import {
  AgentWorkbenchSessionList,
  AgentWorkbenchSidebarNewSessionButton,
  AgentWorkbenchSidebarSearch,
} from "./AgentWorkbenchSidebarNavigation";

export interface AgentWorkbenchExploreItem {
  id: string;
  title: string;
  imageUrl?: string;
  subtitle?: string;
  mediaVariant?: AgentWorkbenchExploreMediaVariant;
  mediaTreatment?: AgentWorkbenchExploreMediaTreatment;
}

export type AgentWorkbenchExploreMediaVariant =
  | "cover"
  | "cover-with-label"
  | "logo";

export type AgentWorkbenchExploreMediaTreatment =
  | "preserve"
  | "monochrome-light"
  | "invert";

export interface AgentWorkbenchExploreCardMediaProps {
  item: AgentWorkbenchExploreItem;
  src?: string;
  className?: string;
  onError?: ReactEventHandler<HTMLImageElement>;
}

export interface AgentWorkbenchExploreGroup {
  id: string;
  label: string;
  items: AgentWorkbenchExploreItem[];
  itemAspectRatio?: number;
}

export interface AgentWorkbenchExploreSidebarLabels {
  navigation: string;
  search: string;
  newSession: string;
  draftSession: string;
  emptySessions: string;
  noResults: string;
  retry?: string;
}

export type AgentWorkbenchExploreSidebarIntent =
  | { type: "change-search"; query: string }
  | { type: "create-session" }
  | { type: "retry-sessions" }
  | { type: "select-session"; sessionId: string }
  | { type: "delete-session"; sessionId: string }
  | { type: "change-group"; groupId: string }
  | { type: "select-item"; groupId: string; item: AgentWorkbenchExploreItem };

export interface AgentWorkbenchExploreSidebarProps {
  sessions: AgentWorkbenchSessionItem[];
  groups?: AgentWorkbenchExploreGroup[];
  config?: AgentWorkbenchSidebarConfig;
  labels: AgentWorkbenchExploreSidebarLabels;
  selectedSessionId?: string;
  searchQuery?: string;
  loading?: boolean;
  error?: string;
  showDraftSession?: boolean;
  deleteSessions?: boolean;
  /** Uses the compact session list treatment for trend workbenches. */
  variant?: AgentWorkbenchVariant;
  defaultGroupId?: string;
  header?: ReactNode;
  footer?: ReactNode;
  className?: string;
  renderItemMedia?: (
    item: AgentWorkbenchExploreItem,
    group: AgentWorkbenchExploreGroup,
  ) => ReactNode;
  onIntent?: (intent: AgentWorkbenchExploreSidebarIntent) => void;
}

export function AgentWorkbenchExploreCardMedia({
  item,
  src = item.imageUrl,
  className,
  onError,
}: AgentWorkbenchExploreCardMediaProps) {
  if (!src) return null;

  const variant = item.mediaVariant ?? "cover";
  const isLogo = variant === "logo";
  const showLabel = variant === "cover-with-label";
  const treatmentClass =
    item.mediaTreatment === "monochrome-light"
      ? "brightness-0 invert"
      : item.mediaTreatment === "invert"
        ? "invert"
        : "";

  return (
    <>
      <span
        className={cn(
          isLogo
            ? "flex size-full items-center justify-center bg-[hsl(var(--monkeys-color-media-surface))] p-5"
            : "block size-full",
          className,
        )}
      >
        <img
          src={src}
          alt=""
          className={cn(
            "transition-transform duration-300 group-hover:scale-[1.03]",
            isLogo
              ? "max-h-full max-w-full object-contain"
              : "size-full object-cover",
            treatmentClass,
          )}
          onError={onError}
        />
      </span>
      {showLabel ? (
        <span
          className="pointer-events-none absolute inset-x-0 bottom-0 px-3 pb-2.5 pt-8 text-[hsl(var(--monkeys-color-media-foreground))]"
          style={{
            backgroundImage:
              "linear-gradient(to top, hsl(var(--monkeys-color-media-overlay) / 0.8), hsl(var(--monkeys-color-media-overlay) / 0.3), transparent)",
          }}
        >
          <span className="block text-base font-semibold leading-tight">
            {item.title}
          </span>
          {item.subtitle ? (
            <span className="mt-0.5 block text-[11px] leading-tight opacity-75">
              {item.subtitle}
            </span>
          ) : null}
        </span>
      ) : null}
    </>
  );
}

export function AgentWorkbenchExploreSidebar({
  sessions,
  groups: configuredGroups,
  config,
  labels,
  selectedSessionId,
  searchQuery = "",
  loading = false,
  error,
  showDraftSession = !selectedSessionId,
  deleteSessions = false,
  variant = "default",
  defaultGroupId,
  header,
  footer,
  className,
  renderItemMedia,
  onIntent,
}: AgentWorkbenchExploreSidebarProps) {
  const resolvedConfig = useMemo(
    () => resolveAgentWorkbenchSidebarConfig(config),
    [config],
  );
  const visibleSections = useMemo(
    () => new Set(resolvedConfig.visibleSections),
    [resolvedConfig.visibleSections],
  );
  const groups = configuredGroups?.length
    ? configuredGroups
    : resolvedConfig.groups;
  const resolvedDefaultGroupId =
    defaultGroupId ?? resolvedConfig.defaultGroupId;
  const [activeGroupId, setActiveGroupId] = useState(
    resolvedDefaultGroupId ?? groups[0]?.id ?? "",
  );
  const activeGroup =
    groups.find((group) => group.id === activeGroupId) ?? groups[0];
  const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
  const draftVisible =
    showDraftSession &&
    (!normalizedQuery ||
      labels.draftSession.toLocaleLowerCase().includes(normalizedQuery));
  const filteredSessions = useMemo(
    () =>
      sessions.filter(
        (session) =>
          !normalizedQuery ||
          session.title.toLocaleLowerCase().includes(normalizedQuery),
      ),
    [normalizedQuery, sessions],
  );

  useEffect(() => {
    if (groups.some((group) => group.id === activeGroupId)) return;
    setActiveGroupId(resolvedDefaultGroupId ?? groups[0]?.id ?? "");
  }, [activeGroupId, groups, resolvedDefaultGroupId]);

  const changeGroup = (groupId: string) => {
    setActiveGroupId(groupId);
    onIntent?.({ type: "change-group", groupId });
  };

  const groupNavigation =
    visibleSections.has("groups") && activeGroup ? (
      <div className="border-t border-border px-4 pb-4 pt-3">
        <Tabs value={activeGroup.id} onValueChange={changeGroup}>
          <TabsList
            className="grid h-10 w-full rounded-[var(--radius)] bg-muted p-0.5 text-foreground"
            style={{
              gridTemplateColumns: `repeat(${groups.length}, minmax(0, 1fr))`,
            }}
          >
            {groups.map((group) => (
              <TabsTrigger
                key={group.id}
                value={group.id}
                className="rounded-md text-sm font-semibold text-foreground data-[state=active]:border data-[state=active]:border-border data-[state=active]:bg-background"
              >
                {group.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {groups.map((group) => (
            <TabsContent key={group.id} value={group.id} className="mt-3">
              <div className="grid grid-cols-2 gap-2">
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    aria-label={item.title}
                    className="group relative aspect-[1.27] overflow-hidden rounded-[var(--radius)] border border-border bg-card text-left transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    style={
                      Number.isFinite(group.itemAspectRatio) &&
                      Number(group.itemAspectRatio) > 0
                        ? { aspectRatio: group.itemAspectRatio }
                        : undefined
                    }
                    onClick={() =>
                      onIntent?.({
                        type: "select-item",
                        groupId: group.id,
                        item,
                      })
                    }
                  >
                    {renderItemMedia?.(item, group) ?? (
                      <AgentWorkbenchExploreCardMedia item={item} />
                    )}
                  </button>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    ) : undefined;

  return (
    <AgentWorkbenchSidebarContainer
      data-sidebar-style="explore"
      data-sidebar-width-percent={resolvedConfig.widthPercent}
      aria-label={labels.navigation}
      variant={variant}
      density={variant === "trend" ? "compact" : undefined}
      header={visibleSections.has("header") ? header : undefined}
      search={
        visibleSections.has("search") ? (
          <AgentWorkbenchSidebarSearch
            value={searchQuery}
            label={labels.search}
            variant={variant}
            onChange={(query) => onIntent?.({ type: "change-search", query })}
          />
        ) : undefined
      }
      primaryAction={
        visibleSections.has("new-session") ? (
          <AgentWorkbenchSidebarNewSessionButton
            label={labels.newSession}
            variant={variant}
            onClick={() => onIntent?.({ type: "create-session" })}
          />
        ) : undefined
      }
      groups={groupNavigation}
      footer={visibleSections.has("footer") ? footer : undefined}
      bodyProps={{ "data-agent-workbench-session-scroll-region": "" }}
      className={className}
      classNames={{ body: "min-h-[160px] flex-[0_1_280px]" }}
    >
      {visibleSections.has("sessions") ? (
        <div className="space-y-2">
          {draftVisible ? (
            <button
              type="button"
              aria-current="page"
              className="block h-10 w-full min-w-0 truncate rounded-[var(--radius)] border border-primary bg-primary/10 px-3 text-left text-sm font-semibold text-foreground"
              onClick={() => onIntent?.({ type: "create-session" })}
            >
              {labels.draftSession}
            </button>
          ) : null}
          {filteredSessions.length > 0 || !draftVisible || loading || error ? (
            <AgentWorkbenchSessionList
              sessions={filteredSessions}
              selectedSessionId={selectedSessionId}
              status={error ? "error" : loading ? "loading" : "ready"}
              error={error}
              query={normalizedQuery}
              emptyLabel={labels.emptySessions}
              noResultsLabel={labels.noResults}
              retryLabel={labels.retry}
              groupByMode={resolvedConfig.navigationMode !== "default"}
              variant={variant}
              capabilities={{ delete: deleteSessions }}
              onSelect={(sessionId) =>
                onIntent?.({ type: "select-session", sessionId })
              }
              onDelete={(sessionId) =>
                onIntent?.({ type: "delete-session", sessionId })
              }
              onRetry={
                labels.retry
                  ? () => onIntent?.({ type: "retry-sessions" })
                  : undefined
              }
            />
          ) : null}
        </div>
      ) : null}
    </AgentWorkbenchSidebarContainer>
  );
}
